/*
 * Lumeer: Modern Data Definition and Processing Platform
 *
 * Copyright (C) since 2017 Lumeer.io, s.r.o. and/or its affiliates.
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import {ChangeDetectionStrategy, Component, HostListener, Input, OnInit} from '@angular/core';

import {Store, select} from '@ngrx/store';

import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import {BehaviorSubject, Observable, combineLatest, of} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';

import {ConstraintData} from '@lumeer/data-filters';
import {uniqueValues} from '@lumeer/utils';

import {AttributesResource, AttributesResourceType, DataResource} from '../../../core/model/resource';
import {AppState} from '../../../core/store/app.state';
import {selectCollectionsByIds} from '../../../core/store/collections/collections.state';
import {selectConstraintData} from '../../../core/store/constraint-data/constraint-data.state';
import {groupDocumentsByCollection} from '../../../core/store/documents/document.utils';
import {selectDocumentsByIds} from '../../../core/store/documents/documents.state';
import {groupLinkInstancesByLinkTypes} from '../../../core/store/link-instances/link-instance.utils';
import {selectLinkInstancesByIds} from '../../../core/store/link-instances/link-instances.state';
import {selectLinkTypeByIdsWithCollections} from '../../../core/store/link-types/link-types.state';
import {Query} from '../../../core/store/navigation/query/query';
import {ViewSettings} from '../../../core/store/view-settings/view-settings';
import {selectViewSettingsByView} from '../../../core/store/view-settings/view-settings.state';
import {selectViewById} from '../../../core/store/views/views.state';
import {emptyEnterAnimation, enterLeftAnimation, enterRightAnimation} from '../../animations';
import {KeyCode, keyboardEventCode} from '../../key-code';
import {getDataResourcesDataIds, sortDataResourcesByViewSettings} from '../../utils/data-resource.utils';
import {attributesResourcesAreSame, getAttributesResourceType} from '../../utils/resource.utils';
import {DialogType} from '../dialog-type';

@Component({
  selector: 'data-resources-detail-modal',
  templateUrl: './data-resources-detail-modal.component.html',
  styleUrls: ['./data-resources-detail-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [enterLeftAnimation, enterRightAnimation, emptyEnterAnimation],
})
export class DataResourcesDetailModalComponent implements OnInit {
  @Input()
  public dataResources: DataResource[];

  @Input()
  public title: string;

  @Input()
  public viewId: string;

  public hasDuplicates: boolean;

  public selectedResourceSubject$ = new BehaviorSubject<AttributesResource>(null);
  public selectedDataResourceIdSubject$ = new BehaviorSubject<string>(null);
  public showDuplicates$ = new BehaviorSubject(true);

  public selectedResource$: Observable<AttributesResource>;
  public selectedDataResource$: Observable<DataResource>;
  public resources$: Observable<AttributesResource[]>;
  public dataResources$: Observable<DataResource[]>;
  public constraintData$: Observable<ConstraintData>;
  public query$: Observable<Query>;
  public viewSettings$: Observable<ViewSettings>;

  public readonly dialogType = DialogType;

  private initialModalsCount: number;

  constructor(
    private bsModalRef: BsModalRef,
    private store$: Store<AppState>,
    private bsModalService: BsModalService
  ) {}

  public ngOnInit() {
    const view$ = this.store$.pipe(select(selectViewById(this.viewId)));
    this.constraintData$ = this.store$.pipe(select(selectConstraintData));
    this.initialModalsCount = this.bsModalService.getModalsCount();
    this.viewSettings$ = view$.pipe(switchMap(view => this.store$.pipe(select(selectViewSettingsByView(view)))));

    const {documentIds: allDocumentIds, linkInstanceIds: allLinkInstanceIds} = getDataResourcesDataIds(
      this.dataResources
    );

    this.hasDuplicates =
      allDocumentIds.length > uniqueValues(allDocumentIds).length ||
      allLinkInstanceIds.length > uniqueValues(allLinkInstanceIds).length;

    const documentsMap$ = this.showDuplicates$.pipe(
      map(showDuplicates => (showDuplicates ? allDocumentIds : uniqueValues(allDocumentIds))),
      switchMap(documentIds =>
        this.store$.pipe(
          select(selectDocumentsByIds(documentIds)),
          map(documents => groupDocumentsByCollection(documents))
        )
      )
    );

    const linkInstancesMap$ = this.showDuplicates$.pipe(
      map(showDuplicates => (showDuplicates ? allLinkInstanceIds : uniqueValues(allLinkInstanceIds))),
      switchMap(linkInstanceIds =>
        this.store$.pipe(
          select(selectLinkInstancesByIds(linkInstanceIds)),
          map(linkInstances => groupLinkInstancesByLinkTypes(linkInstances))
        )
      )
    );

    const collections$ = documentsMap$.pipe(
      map(documentsMap => Object.keys(documentsMap)),
      switchMap(collectionIds => this.store$.pipe(select(selectCollectionsByIds(collectionIds))))
    );

    const linkTypes$ = linkInstancesMap$.pipe(
      map(linkInstancesMap => Object.keys(linkInstancesMap)),
      switchMap(linkTypeIds => this.store$.pipe(select(selectLinkTypeByIdsWithCollections(linkTypeIds))))
    );

    this.resources$ = combineLatest([collections$, linkTypes$]).pipe(
      map(([collections, linkTypes]) => [...collections, ...linkTypes])
    );
    this.selectedResource$ = combineLatest([this.resources$, this.selectedResourceSubject$]).pipe(
      map(
        ([resources, selectedResource]) =>
          (selectedResource && resources.find(res => attributesResourcesAreSame(res, selectedResource))) || resources[0]
      )
    );

    this.dataResources$ = combineLatest([this.selectedResource$, this.viewSettings$, this.constraintData$]).pipe(
      switchMap(([resource, viewSettings, constraintData]) => {
        const resourceMap = {[resource?.id]: resource};
        if (resource && getAttributesResourceType(resource) === AttributesResourceType.Collection) {
          return documentsMap$.pipe(
            map(documentsMap => documentsMap[resource.id]),
            map(documents =>
              sortDataResourcesByViewSettings(
                documents,
                resourceMap,
                AttributesResourceType.Collection,
                viewSettings?.attributes,
                constraintData
              )
            )
          );
        } else if (resource) {
          return linkInstancesMap$.pipe(
            map(linkInstancesMap => linkInstancesMap[resource.id]),
            map(linkInstances =>
              sortDataResourcesByViewSettings(
                linkInstances,
                resourceMap,
                AttributesResourceType.LinkType,
                viewSettings?.attributes,
                constraintData
              )
            )
          );
        }
        return of([]);
      })
    );

    this.selectedDataResource$ = combineLatest([this.dataResources$, this.selectedDataResourceIdSubject$]).pipe(
      map(([dataResources, selectedDataResourceId]) =>
        dataResources.find(dataResource => dataResource.id === selectedDataResourceId)
      )
    );
  }

  public hideDialog() {
    this.bsModalRef.hide();
  }

  public onSelectDataResource(dataResource: DataResource) {
    this.selectedDataResourceIdSubject$.next(dataResource.id);
  }

  public onSelectResource(resource: AttributesResource) {
    this.selectedResourceSubject$.next(resource);
  }

  public resetSelectedDataResource() {
    this.selectedDataResourceIdSubject$.next(null);
  }

  @HostListener('document:keydown', ['$event'])
  public onKeyDown(event: KeyboardEvent) {
    // when another dialog is presented in top of this dialog, we don't want to listen on escape events
    if (
      keyboardEventCode(event) === KeyCode.Escape &&
      this.initialModalsCount >= this.bsModalService.getModalsCount()
    ) {
      this.hideDialog();
    }
  }
}
